import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useWindowSize } from "../../../../../../../hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import API from "../../../../../../../service/api";
import WS from "../../../../../../../service/ws";
import { TabsViewer } from "../../../../../../../utils/TabsViewer";
import { LogViewer } from "../../../../../../../components/LogViewer";
import { Data } from "../../../../../../../service/global";
import { objUpdate } from "../../../../../../../service/utils";
import { MachineDetails } from "./MachineDetails";
import { BoxesList, BoxesListHandle } from "./BoxesList";
import { MDBIcon } from "mdb-react-ui-kit";
import { StatusBar } from "./StatusBar";
import { OperatorNavigationBar } from "../../../../../../../components/OperatorNavigationBar";
import { ErrorScreen, ErrorScreenProps } from "../../../../../../../components/errors/ErrorScreen";
import { LoadingSpinner } from "../../../../../../../components/LoadingSpinner";
import OpMachineListItemDTO from "@common/dtos/OpMachineListItemDTO";
import OpMachineDTO from "@common/dtos/OpMachineDTO";
import ReportSelectorModal from "../../../../../../../components/modals/ReportSelectorModal";

export interface MachineViewerPageProps {
    groupId: number;
    groupName: string;
    locationId: number;
    locationName: string;
    machines: OpMachineListItemDTO[];
    standalone: boolean;
}
export const MachineViewerPage = ({ groupId, locationId, groupName, locationName, machines, standalone }: MachineViewerPageProps) => {
	const params = useParams();
    const id = useMemo(() => Number(params.id), [params]);
	const { t } = useTranslation();
    const { innerWidth } = useWindowSize();

	const [machine, setMachine] = useState<OpMachineDTO|null>(null);
	const [fetching, setFetching] = useState(false);
	const [needsToFetch, setNeedsToFetch] = useState(true);
	const [products, setProducts] = useState([]);
	const [error, setError] = useState<ErrorScreenProps|null>(null);
    const [reportSelectorOpened, setReportSelectorOpened] = useState(false);

    const machineName = useMemo(() => machine?.name || machines.find(m => m.id == id)?.name || "?", [machines, machine, id]);
    const machineAPIUri = useMemo(() => `/api/groups/${groupId}/${locationId}/${id}`, [groupId, locationId, id]);
    const isConnected = useMemo(() => machine?.connection == true, [machine]);
    const canEditProduct = useMemo(() => (machine?.permissions?.ProductEdit && machine?.connection) ?? false, [machine]);
    const canEditPrice = useMemo(() => (machine?.permissions?.PriceEdit && machine?.connection) ?? false, [machine]);
    const canEditQnty = useMemo(() => (machine?.permissions?.QNTYEdit && machine?.connection) ?? false, [machine]);

    const boxesListRef = useRef<BoxesListHandle|null>(null);

    useEffect(() => {
        if (!groupId || !locationId || !id) return;

        WS.emit("machine", {groupId: groupId, locationId: locationId, machineId: id});

        const unlisten = WS.listen("machine", ({groupId, locationId, machineId, updates}: {groupId: number, locationId: number, machineId: number, updates: any}) => {
            if (groupId !== groupId || locationId !== locationId || machineId !== id) return;
            setMachine(prev => objUpdate(prev, updates));
        });

        return () => {
            unlisten();
            WS.emit("machine", {});
        }
    }, [groupId, locationId, id]);

	const fetch = useCallback(() => {
		setFetching(true);
		setNeedsToFetch(false);

        API.GET('/api/products')
            .then(setProducts)
            .catch(console.error)
            .finally(() => {
                API.GET(`${machineAPIUri}`)
                .then(setMachine)
                .catch(err => {
                    setError({
                        title: err.code == 404 ? t('errors:not_found') : t('errors:an_issue_occured'),
                        description: err.code == 404
                            ? t('errors:machine_not_found')
                            : t('errors:machine_issue'),
                        tryAgain: err.code == 404 ? undefined : () => setNeedsToFetch(true)
                    })
                })
                .finally(() => setFetching(false));
            })
	}, [machineAPIUri]);

	useEffect(() => {
		if (needsToFetch && !fetching) fetch();
	}, [needsToFetch, fetching]);
    
    const StatusBarSection = useMemo(() => !machine ? null : (
        <StatusBar machine={machine} groupId={groupId} locationId={locationId} apiURI={machineAPIUri}/>
    ), [machine, groupId, locationId, machineAPIUri]);

    const BoxesListSection = useMemo(() => !machine ? null : (
        <BoxesList ref={boxesListRef} apiURI={machineAPIUri} machine={machine} products={products} canEditPrice={canEditPrice} canEditProduct={canEditProduct} canEditQnty={canEditQnty}/>
    ), [machine, products, canEditPrice, canEditProduct, canEditQnty, machineAPIUri]);

    const MachineDetailsSection = useMemo(() => !machine ? null : (
        <MachineDetails apiURI={machineAPIUri} machine={machine} isConnected={isConnected}/>
    ), [machine, isConnected]);

    const LogViewerSection = useMemo(() => (
        <LogViewer groupId={groupId} locationId={locationId} machineId={id} />
    ), [groupId, locationId, id]);

    const ReportPopupSection = useMemo(() =>
        machine?.value !== undefined
        ?   <span role={machine?.allowReport ? "button" : "none"} className="badge badge-success align-items-center" onClick={() => setReportSelectorOpened(machine?.allowReport ?? false)}>
                <MDBIcon icon="hand-holding-dollar" size="lg" fixed color="primary" className="me-1"/>
                {machine.value}
            </span>
        :   null
    , [groupId, locationId, machine, innerWidth]);


	return (
		<div className="d-flex flex-fill flex-column w-100 p-0 justify-content-center align-items-center">
            <ReportSelectorModal
                open={reportSelectorOpened}
                onClose={() => setReportSelectorOpened(false)}
                groupId={groupId}
                locationId={locationId}
                machineId={machine?.id ?? -1}
            />
            <OperatorNavigationBar
                backTo={standalone ? '../../' : `../locations/${locationId}`}
                right={ ReportPopupSection }
                links={[
                    { name: groupName, icon: 'layer-group', to: `../..` },
                    { name: locationName, icon: 'map-marker-alt', to: `..` },
                    { name: machineName || "", icon: 'broadcast-tower', to: ``, onClick: () => {
                        if (boxesListRef.current) boxesListRef.current.closeBoxes();
                    } }
                ]}
            />

            {error && <div className="flex-fill my-5"><ErrorScreen {...error}/></div>}
            {fetching && <LoadingSpinner/>}

            {(machine && !fetching) && <>
                {innerWidth < 1000 && (
                    <div className="d-flex flex-column h-100 w-100 flex-fill justify-content-center align-items-center overflow-hidden">
                        <TabsViewer
                            id="machine"
                            defaultTab="boxes"
                            tabs={[
                                { name: "machineinfo", title: t('machine'), component: MachineDetailsSection },
                                { name: "boxes", title: t('boxes'), component: (
                                    <div className="d-flex flex-column flex-fill">
                                        { BoxesListSection }
                                        <div className="d-flex flex-fill"></div>
                                    </div>
                                    
                                )},
                                { name: "logs", title: t('logs'), component: LogViewerSection }
                            ]}
                        />
                        { StatusBarSection }
                    </div>
                    
                )}
                {(innerWidth >= 1000 && innerWidth < 1200) && (
                    <div className="d-flex flex-row w-100 flex-fill overflow-auto">
                        <div className="d-flex flex-column align-items-center col-8">
                            <TabsViewer
                                id="machine"
                                defaultTab="boxes"
                                tabs={[
                                    { name: "machineinfo", title: t('machine'), component: MachineDetailsSection },
                                    { name: "boxes", title: t('boxes'), component: (
                                        <div className="d-flex flex-column flex-fill">
                                            { BoxesListSection }
                                            <div className="d-flex flex-fill"></div>
                                        </div>
                                    )}
                                ]}
                            />
                            { StatusBarSection }
                        </div>
                        <div className="d-flex flex-column flex-fill col-4 ms-2">
                            {/* <h5 className="w-100" style={{textAlignLast: 'center'}}>{t('logs')}</h5> */}
                            { LogViewerSection }
                        </div>
                    </div>
                    
                )}

                {innerWidth >= 1200 && (
                    <div className="d-flex flex-row w-100 flex-fill overflow-hidden">
                        <div className="d-flex flex-column align-items-center col-4">
                            <div className="d-flex flex-column overflow-auto w-100">
                                { MachineDetailsSection }
                            </div>
                        </div>
                        <div className="d-flex flex-column align-items-center col-4">
                            <div className="d-flex flex-column flex-fill w-100">
                                { BoxesListSection }
                                <div className="d-flex flex-fill"></div>
                                { StatusBarSection }
                            </div>
                        </div>
                        <div className="d-flex flex-column flex-fill col-4 ms-2">
                            { LogViewerSection }
                        </div>
                    </div>
                )}
            
            </>}
		</div>
	);
};